import {
  Component,
  Input,
  OnInit,
  inject,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';
import { AuthService } from 'src/app/auth.service';
import { FirebaseService } from 'src/app/services/firebase.service';

interface ScheduleEmployee {
  name: string;
  Sun?: string;
  Mon?: string;
  Tue?: string;
  Wed?: string;
  Thu?: string;
  Fri?: string;
  Sat?: string;
  totalHours?: number;
  daysOff?: number;
}

interface PublishedSchedule {
  result: ScheduleEmployee[];
  weekStart: string;
  weekEnd: string;
  dates: { [key: string]: string };
  publishedAt: any;
  publishedBy: string;
  status?: string;
  weekRange?: string;
  orgId?: string;
}

@Component({
  selector: 'app-view-published-schedule',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './view-published-schedule.component.html',
  styleUrls: ['./view-published-schedule.component.css'],
})
export class ViewPublishedScheduleComponent implements OnInit, OnChanges {
  private readonly firestore = inject(Firestore);
  private readonly auth = inject(AuthService);

  constructor(private firebaseService: FirebaseService) {}

  @Input() schedule: PublishedSchedule | null = null;

  isLoading = true;
  error: string | null = null;
  employeeName: string | null = null;
  isManager = false;
  orgName: string = '';

  dayKeys = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  weekDates: string[] = []; // formatted week dates for printing

  async ngOnInit() {
    const user = this.auth.getUser();
    if (!user || !user.orgId) {
      this.error = 'Please log in to view schedules';
      this.isLoading = false;
      return;
    }

    this.employeeName = this.auth.getUserName();
    this.isManager = this.auth.isManager;
    await this.loadOrgName();

    if (this.schedule) {
      this.prepareWeekDates();
      this.isLoading = false;
    } else {
      await this.loadPublishedSchedule();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    console.log('ngOnChanges triggered', changes);
    if (changes['schedule']) {
      console.log('Schedule changed:', changes['schedule'].currentValue);
      this.prepareWeekDates();
    }
  }

  private async loadPublishedSchedule() {
    const orgId = this.auth.getUsersOrgId();
    if (!orgId) {
      this.error = 'No organization ID found';
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    try {
      const scheduleRef = doc(
        this.firestore,
        'schedules',
        `${orgId}-published`
      );
      const scheduleSnap = await getDoc(scheduleRef);

      if (scheduleSnap.exists()) {
        this.schedule = scheduleSnap.data() as PublishedSchedule;
        this.prepareWeekDates();
      } else {
        this.error = 'No schedule has been published yet';
      }
    } catch (err) {
      console.error('Error loading schedule:', err);
      this.error = 'Failed to load schedule. Please try again.';
    } finally {
      this.isLoading = false;
    }
  }

  private prepareWeekDates() {
    console.log('prepareWeekDates called');
    console.log('schedule:', this.schedule);
    console.log('schedule.dates:', this.schedule?.dates);

    if (!this.schedule) {
      console.log('No schedule');
      return;
    }

    // Check if dates is an array (already calculated) or an object (from database)
    if (this.schedule.dates) {
      if (Array.isArray(this.schedule.dates)) {
        // It's already an array, use it directly
        console.log('Using schedule.dates array');
        this.weekDates = this.schedule.dates;
        console.log('weekDates from array:', this.weekDates);
      } else {
        // It's an object with day keys
        this.weekDates = this.dayKeys.map(
          (day) => this.schedule?.dates[day] || ''
        );

        // Check if any dates are actually filled
        const hasValidDates = this.weekDates.some(
          (date) => date && date.trim() !== ''
        );

        if (hasValidDates) {
          console.log('Using schedule.dates object');
          console.log('weekDates from dates object:', this.weekDates);
        } else {
          // Dates object exists but is empty, calculate from weekStart
          console.log(
            'Dates object is empty, calculating from weekStart:',
            this.schedule.weekStart
          );
          this.calculateWeekDates();
          console.log('weekDates calculated:', this.weekDates);
        }
      }
    }
    // No dates property, calculate from weekStart
    else if (this.schedule.weekStart) {
      console.log(
        'No dates object, calculating from weekStart:',
        this.schedule.weekStart
      );
      this.calculateWeekDates();
      console.log('weekDates calculated:', this.weekDates);
    }
  }

  private calculateWeekDates() {
    if (!this.schedule?.weekStart) return;

    const startDate = new Date(this.schedule.weekStart + 'T00:00:00');
    this.weekDates = [];

    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      // Format as YYYY-MM-DD to match the format expected by the date pipe
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;

      this.weekDates.push(dateStr);
    }

    // ✅ Store as an array in schedule.dates
    if (this.schedule) {
      this.schedule.dates = [...this.weekDates] as any;
    }
  }

  // --- Modern print layout ---
  printSchedule(): void {
    if (!this.schedule) return;

    const html = this.generatePrintHTML();
    const printWindow = window.open('', '', 'width=1000,height=700');
    if (!printWindow) return;
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  }

  async loadOrgName() {
    const orgId = this.auth.getUsersOrgId();
    if (!orgId) {
      this.orgName = 'Organization';
      return;
    }

    const orgName = await this.firebaseService.getOrgNameByOrgId(orgId);
    this.orgName = orgName || 'Organization';
  }

  get isSpectator(): boolean {
    return this.auth.isSpectator;
  }

  get isEmployee(): boolean {
    return this.auth.isEmployee;
  }

  // --- Build print HTML ---
  generatePrintHTML(): string {
    const currentDate = new Date().toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    const orgId = this.auth.getUsersOrgId();

    // Local date formatter
    const formatDate = (dateStr: string) => {
      if (!dateStr) return '';
      const [y, m, d] = dateStr.split('-').map(Number);
      const date = new Date(y, (m ?? 1) - 1, d ?? 1);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    };

    // Build rows
    let tableRows = '';
    this.schedule!.result.forEach((emp) => {
      tableRows += `
        <tr>
          <td style="font-weight: bold;">${emp.name}</td>
          <td>${emp.Sun || 'off'}</td>
          <td>${emp.Mon || 'off'}</td>
          <td>${emp.Tue || 'off'}</td>
          <td>${emp.Wed || 'off'}</td>
          <td>${emp.Thu || 'off'}</td>
          <td>${emp.Fri || 'off'}</td>
          <td>${emp.Sat || 'off'}</td>
        </tr>`;
    });

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Weekly Schedule</title>
        <style>
          @media print {
            body { margin: 0; }
            @page { margin: 0.5in; }
          }

          body {
            font-family: Arial, sans-serif;
            padding: 20px;
            color: #333;
          }

          .header {
            text-align: center;
            margin-bottom: 20px;
          }

          h1 {
            margin: 0;
            font-size: 22px;
          }

          .subtext {
            color: #666;
            font-size: 13px;
            margin-top: 5px;
          }

          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 20px;
          }

          th {
            background-color: #f8f9fa;
            color: #333;
            padding: 10px 6px;
            border: 1px solid #dee2e6;
            font-size: 12px;
            text-align: center;
          }

          th .date-header {
            display: block;
            font-size: 10px;
            font-weight: normal;
            margin-top: 2px;
          }

          td {
            padding: 8px 6px;
            border: 1px solid #dee2e6;
            text-align: center;
            font-size: 11px;
          }

          tr:nth-child(even) {
            background-color: #f8f9fa;
          }

          tr:hover {
            background-color: #e9ecef;
          }

          .footer {
            text-align: center;
            font-size: 11px;
            color: #777;
            margin-top: 25px;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Weekly Employee Schedule</h1>
          <div class="subtext">
            ${this.orgName} &nbsp;|&nbsp;
            Week of ${this.getWeekLabel()}<br>
            Generated on ${currentDate}
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Sun<span class="date-header">${formatDate(
                this.weekDates[0]
              )}</span></th>
              <th>Mon<span class="date-header">${formatDate(
                this.weekDates[1]
              )}</span></th>
              <th>Tue<span class="date-header">${formatDate(
                this.weekDates[2]
              )}</span></th>
              <th>Wed<span class="date-header">${formatDate(
                this.weekDates[3]
              )}</span></th>
              <th>Thu<span class="date-header">${formatDate(
                this.weekDates[4]
              )}</span></th>
              <th>Fri<span class="date-header">${formatDate(
                this.weekDates[5]
              )}</span></th>
              <th>Sat<span class="date-header">${formatDate(
                this.weekDates[6]
              )}</span></th>
            </tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>

        <div class="footer">
          Published by ${this.schedule?.publishedBy || 'Manager'} on ${
      this.schedule?.publishedAt?.toDate
        ? this.schedule?.publishedAt?.toDate().toLocaleString()
        : ''
    }
        </div>
      </body>
      </html>
    `;
  }

  // --- Utility methods ---
  private parseYMDLocal(ymd: string): Date {
    const [y, m, d] = ymd.split('-').map(Number);
    return new Date(y, (m ?? 1) - 1, d ?? 1);
  }

  getWeekLabel(): string {
    if (!this.schedule) return '';
    const start = this.parseYMDLocal(this.schedule.weekStart);
    const end = this.parseYMDLocal(this.schedule.weekEnd);
    return `${start.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    })} - ${end.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })}`;
  }

  isWorkDayFor(emp: ScheduleEmployee, day: string): boolean {
    const shift = (emp as Record<string, any>)[day];
    return shift && shift !== 'off' && shift.toLowerCase() !== 'requested off';
  }

  isRequestedOffFor(emp: ScheduleEmployee, day: string): boolean {
    const shift = (emp as Record<string, any>)[day];
    return shift?.toLowerCase() === 'requested off';
  }

  getShift(emp: ScheduleEmployee, day: string): string {
    const shift = (emp as Record<string, any>)[day];
    if (!shift) return 'off';

    // ✅ Replace long "requested off" with short "R/O" for both print + view
    if (shift.toLowerCase() === 'requested off') return 'R/O';

    return shift;
  }
}
